import React, { useState } from "react";
import closeIcon from "../../assets/images/close.png";
import { ItemImage } from "../../models/item_model";

interface ImageModalProps {
  images: ItemImage[];
  initialIndex?: number;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  const scale = zoomed ? 2 : 1;
  const currentImage = images[currentIndex].document_url;

  const handleToggleZoom = () => {
    setZoomed(!zoomed);
    if (zoomed) setTranslate({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!zoomed) return;
    setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStart) return;
    setTranslate({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragStart(null);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setZoomed(false);
    setTranslate({ x: 0, y: 0 });
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setZoomed(false);
    setTranslate({ x: 0, y: 0 });
  };

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        style={styles.content}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <img
          src={closeIcon}
          alt="Close"
          style={styles.close}
          onClick={onClose}
        />

        {/* Left Arrow */}
        {images.length > 1 && (
          <div style={styles.arrowLeft} onClick={handlePrev}>
            &#10094;
          </div>
        )}

        {/* Image */}
        <img
          src={currentImage}
          alt={`Preview ${currentIndex + 1}`}
          style={{
            ...styles.image,
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            cursor: zoomed ? "grab" : "zoom-in",
          }}
          onClick={handleToggleZoom}
          onMouseDown={handleMouseDown}
        />

        {/* Right Arrow */}
        {images.length > 1 && (
          <div style={styles.arrowRight} onClick={handleNext}>
            &#10095;
          </div>
        )}

        {/* Index Indicator */}
        {images.length > 1 && (
          <div style={styles.indexIndicator}>
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.7)",
    backdropFilter: "blur(8px)",
    zIndex: 5000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    position: "relative",
    width: "90%",
    maxWidth: 800,
    height: "90%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  close: {
    position: "fixed", 
    top: 24,         
    right: 24,       
    width: 36,
    height: 36,
    cursor: "pointer",
    zIndex: 6000,      
    borderRadius: "50%",
    padding: 4,
  },
  arrowLeft: {
    position: "absolute",
    left: 16,
    fontSize: 48,
    color: "white",
    cursor: "pointer",
    zIndex: 5001,
    userSelect: "none",
  },
  arrowRight: {
    position: "absolute",
    right: 16,
    fontSize: 48,
    color: "white",
    cursor: "pointer",
    zIndex: 5001,
    userSelect: "none",
  },
  image: {
    maxWidth: "95%",
    maxHeight: "95%",
    objectFit: "contain",
    borderRadius: 8,
    userSelect: "none",
    transition: "transform 0.3s",
  },
  indexIndicator: {
    position: "absolute",
    bottom: 16,
    color: "white",
    fontSize: 16,
    background: "rgba(0,0,0,0.4)",
    padding: "4px 8px",
    borderRadius: 8,
    zIndex: 5001,
  },
};


export default ImageModal;
